import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, USE_MOCK_AUTH, ApiError } from '@/lib/api';
import { MOCK_USER } from '@/data/mock';
import type { CurrentUser, Workspace, WorkspaceRole } from '@/types';

/* ----------------------------------------------------------------------------
 * Auth context — login / logout / me + active-workspace switch.
 * Backs onto the deployed cookie-session endpoints (human-auth layer):
 *   POST /v1/auth/login             { email, password } -> sets session cookie
 *   POST /v1/auth/logout            (CSRF-gated) revokes the session
 *   GET  /v1/auth/me                -> HumanContext (user + workspaces + role)
 *   POST /v1/auth/switch-workspace  (CSRF-gated) changes the active workspace
 * REAL auth is the default. Only VITE_USE_MOCK_AUTH=true (never set in the
 * production build) resolves against MOCK_USER so the shell can run without a
 * backend. Screen DATA is gated separately by USE_MOCK_DATA.
 * -------------------------------------------------------------------------- */

/* The /auth endpoints return the backend HumanContext projection:
 *   { user:{id,email}, active_workspace_id:int|null, role, workspaces:[{id,slug,display_name,role}] }
 * which differs from the SPA CurrentUser shape. `adaptContext` bridges the two.
 * Naming priority (owner directive 2026-07-15): user display_name, else the
 * active workspace/business name, else the email local-part — the raw login
 * identifier is only ever a last resort. Entitlements default to the demo
 * engine set (screen data is gated separately by USE_MOCK_DATA). */
const DEMO_ENTITLEMENTS = ['sales', 'seo', 'ai', 'communications'];

interface HumanContextDto {
  user: { id: number | string; email: string; display_name?: string | null };
  active_workspace_id: number | string | null;
  role: string;
  workspaces: Array<{
    id: number | string;
    slug?: string;
    display_name?: string;
    role: string;
  }>;
}

function adaptContext(dto: HumanContextDto): CurrentUser {
  const email = dto.user?.email ?? '';
  const workspaces: Workspace[] = (dto.workspaces ?? []).map((w) => ({
    id: String(w.id),
    account_id: w.slug ?? '',
    name: w.display_name || w.slug || `Workspace ${w.id}`,
    role: (w.role as WorkspaceRole) ?? 'readonly',
    entitlements: DEMO_ENTITLEMENTS,
  }));
  const activeId =
    dto.active_workspace_id != null ? String(dto.active_workspace_id) : workspaces[0]?.id ?? '';
  const displayName = (dto.user?.display_name ?? '').trim();
  const workspaceName = workspaces.find((w) => w.id === activeId)?.name ?? '';
  const username = email.split('@')[0] || email;
  return {
    id: String(dto.user?.id ?? ''),
    name: displayName || workspaceName || username,
    // Salutations use the first name only when we have a real display name;
    // a workspace/business name reads naturally in full.
    greeting_name: displayName ? displayName.split(/\s+/)[0] : workspaceName || username,
    email,
    workspaces,
    active_workspace_id: activeId,
  };
}

interface AuthState {
  user: CurrentUser | null;
  loading: boolean;
  activeWorkspace: Workspace | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  switchWorkspace: (workspaceId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

const ACTIVE_WS_KEY = 'bc-active-workspace';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
  // Safe here: main.tsx nests <AuthProvider> INSIDE <QueryClientProvider>, so
  // this resolves to the module singleton from @/lib/queryClient. Used to wipe
  // cached screen data on workspace switch / logout — no query is keyed by
  // workspace, so without a clear() workspace A's rows would briefly render
  // under workspace B (M5).
  const queryClient = useQueryClient();

  const loadMe = useCallback(async () => {
    if (USE_MOCK_AUTH) {
      setUser(MOCK_USER);
      setActiveWorkspaceId(
        localStorage.getItem(ACTIVE_WS_KEY) ?? MOCK_USER.active_workspace_id,
      );
      return;
    }
    try {
      const me = adaptContext(await api.get<HumanContextDto>('/auth/me'));
      setUser(me);
      setActiveWorkspaceId(localStorage.getItem(ACTIVE_WS_KEY) ?? me.active_workspace_id);
    } catch (err) {
      // 401 (no/expired session) or any other failure: unauthenticated, but
      // never crash the shell — ProtectedRoute redirects to /login.
      if (!(err instanceof ApiError)) {
        console.error('auth/me failed', err);
      }
      setUser(null);
    }
  }, []);

  useEffect(() => {
    void (async () => {
      await loadMe();
      setLoading(false);
    })();
  }, [loadMe]);

  const login = useCallback(
    async (email: string, password: string) => {
      // Defense-in-depth: wipe any residual cached data before adopting a new
      // session, so nothing from a prior login can bleed into this one (no
      // query is workspace/user-keyed). The expiry path already hard-reloads.
      queryClient.clear();
      if (USE_MOCK_AUTH) {
        setUser(MOCK_USER);
        setActiveWorkspaceId(MOCK_USER.active_workspace_id);
        return;
      }
      // The login response IS the HumanContext (+ csrf_token); adopt it directly
      // so we don't need a second round-trip to /auth/me. The session + CSRF
      // cookies are set by the response.
      const ctx = adaptContext(await api.post<HumanContextDto>('/auth/login', { email, password }));
      setUser(ctx);
      setActiveWorkspaceId(ctx.active_workspace_id);
      localStorage.removeItem(ACTIVE_WS_KEY);
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    if (!USE_MOCK_AUTH) {
      try {
        await api.post('/auth/logout');
      } catch {
        /* best-effort — clear client state regardless */
      }
    }
    setUser(null);
    setActiveWorkspaceId(null);
    localStorage.removeItem(ACTIVE_WS_KEY);
    // Wipe all cached screen data so nothing survives into the next session.
    queryClient.clear();
  }, [queryClient]);

  const switchWorkspace = useCallback(
    async (workspaceId: string) => {
      // Optimistic local switch first (keeps the UI responsive)…
      setActiveWorkspaceId(workspaceId);
      localStorage.setItem(ACTIVE_WS_KEY, workspaceId);
      if (USE_MOCK_AUTH) {
        queryClient.clear();
        return;
      }
      // …then persist server-side so the session's active workspace + role follow.
      try {
        const ctx = adaptContext(
          await api.post<HumanContextDto>('/auth/switch-workspace', {
            workspace_id: Number(workspaceId),
          }),
        );
        setUser(ctx);
        // The session now points at the new workspace — drop every cached
        // query so no screen briefly shows the previous workspace's data
        // (active queries refetch immediately against the new session).
        queryClient.clear();
      } catch {
        /* best-effort — local state already reflects the choice. The server
         * session (and therefore all data) is still the previous workspace,
         * so the cache is NOT cleared: cached rows still match the session. */
      }
    },
    [queryClient],
  );

  const activeWorkspace = useMemo(() => {
    if (!user) return null;
    return (
      user.workspaces.find((w) => w.id === activeWorkspaceId) ??
      user.workspaces[0] ??
      null
    );
  }, [user, activeWorkspaceId]);

  const value = useMemo<AuthState>(
    () => ({ user, loading, activeWorkspace, login, logout, switchWorkspace, refresh: loadMe }),
    [user, loading, activeWorkspace, login, logout, switchWorkspace, loadMe],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
